import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { CommandPalette } from '@/components/command/CommandPalette';
import { A11yReport } from '@/components/dev/A11yReport';
import { BuyerSidebar } from '@/components/buyer/BuyerSidebar';
import { MobileBottomNav } from '@/components/navigation/MobileBottomNav';
import { BuyerMobileHeader } from '@/components/buyer/BuyerMobileHeader';
import { BuyerTopNav } from '@/components/buyer/BuyerTopNav';
import { SidebarProvider, useSidebar } from '@/contexts/SidebarContext';
import { LayoutProvider } from '@/contexts/LayoutContext';
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner';
import { SkipLink } from '@/components/accessibility/SkipLink';
import { cn } from '@/lib/utils';
import { PageTransition } from '@/components/layout/PageTransition';
import { useKeyboardShortcutsContext } from '@/hooks/useKeyboardShortcutsContext';

// PERFORMANCE: Lazy load non-critical components
const AIAssistant = lazy(() => import('@/components/ai/AIAssistant').then(m => ({ default: m.AIAssistant })));
const ComparisonFloatingButton = lazy(() => import('@/components/supplier/ComparisonFloatingButton'));

function BuyerLayoutContent() {
  const { isCollapsed } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();

  useKeyboardShortcutsContext([
    { key: 'd', description: 'Dashboard', action: () => navigate('/buyer/dashboard') },
    { key: 'i', description: 'Inventory', action: () => navigate('/buyer/inventory') },
    { key: 's', description: 'Suppliers', action: () => navigate('/buyer/suppliers') },
    { key: 'q', description: 'Quotes', action: () => navigate('/buyer/quotes') },
    { key: 'o', description: 'Orders', action: () => navigate('/buyer/orders') },
    { key: 'c', description: 'Create RFQ', action: () => navigate('/buyer/rfq/new') },
    { key: 'n', description: 'Add Inventory', action: () => navigate('/buyer/inventory') },
    {
      key: '/',
      description: 'Focus Search',
      action: () => {
        const searchInput = document.querySelector('[data-search-input]') as HTMLInputElement;
        searchInput?.focus();
      },
    },
  ]);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
      <>
      <CommandPalette />
      <A11yReport />
      <div className="flex h-screen w-full overflow-hidden bg-background">
      <SkipLink />
      {/* Desktop Sidebar - Navigation */}
        <aside
          className={cn(
            "hidden md:flex bg-white transition-all duration-250 ease-in-out border-r border-border h-screen",
            isCollapsed ? "w-[72px]" : "w-[280px]"
          )}
          aria-label="Main navigation"
        >
          <div className="sticky top-0 h-screen w-full">
            <BuyerSidebar />
          </div>
        </aside>
      
      {/* Main Content */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {/* Impersonation Banner */}
        <div className="px-4 pt-4 md:px-6">
          <ImpersonationBanner />
        </div>
        
        {/* Mobile Header (only on mobile) */}
        <header className="md:hidden">
          <BuyerMobileHeader />
        </header>
        
        {/* Desktop Top Nav (only on desktop) */}
        <header className="hidden md:block">
          <BuyerTopNav />
        </header>
        
      {/* Page Content */}
      <main
        id="main-content"
        data-scroll-container
        className="ios-smooth-scroll min-h-0 min-w-0 flex-1 overflow-auto bg-background px-4 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] pt-4 sm:px-6 md:pb-8 md:pt-6 lg:px-8"
      >
          <PageTransition variant="fade">
            <Outlet />
          </PageTransition>
        </main>
        
        {/* Mobile Bottom Navigation */}
        <nav className="md:hidden" aria-label="Mobile navigation">
          <MobileBottomNav />
        </nav>
      </div>
      
      {/* AI Assistant - lazy loaded for faster initial render */}
      <Suspense fallback={null}>
        <AIAssistant />
      </Suspense>

      {/* Comparison Floating Button - lazy loaded */}
      <Suspense fallback={null}>
        <ComparisonFloatingButton />
      </Suspense>
    </div>
    </>
  );
}

export function BuyerLayout() {
  return (
    <SidebarProvider>
      <LayoutProvider>
        <BuyerLayoutContent />
      </LayoutProvider>
    </SidebarProvider>
  );
}
